'use client'

import React, { useState, useRef, useMemo } from 'react'
import styles from './BottomSection.module.scss'
import { faqData } from './BottomSection.data'
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useWishlist } from '@context/WishlistContext';
import { getContestUI } from '@helpers/contestLogic';
import assetLoader from '@utils/internalImageLoder';
import { useGlobalLoader } from '@hooks/useGlobalLoader';
import { EventType, trackEventWithCommonProps } from '@utils/eventTracker';
import { PAGE_TYPES } from '@constants/pageTypes';

const BottomSection = ({ contestState, campaignData, upcomingCampaignData, cityName, campaign_stage, car_policyholder, campaignOptInData, isUserParticipated }: any) => {

    const router = useRouter();
    const { showLoader } = useGlobalLoader();
    const { wishlistItems } = useWishlist();
    const campaignWishlistCount = wishlistItems.length;

    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
        const initial: Record<string, boolean> = {}
        faqData.forEach(section => {
            if (section.defaultExpanded) {
                initial[section.id] = true
            }
        })
        return initial
    })
    const [showAllContent, setShowAllContent] = useState<Record<string, boolean>>({})
    const faqRefs = useRef<Record<string, HTMLDivElement | null>>({})

    const isContestClosedWithNoUpcoming = useMemo(() => {
        return !upcomingCampaignData && (contestState === "NO_ACTIVE_CAMPAIGN" || contestState === "COMPLETED");
    }, [upcomingCampaignData, contestState]);

    const uiConfig = useMemo(() => {
        return getContestUI(campaignData, upcomingCampaignData, contestState, campaignWishlistCount, false, campaignOptInData, isUserParticipated);
    }, [campaignData, upcomingCampaignData, contestState, campaignWishlistCount, campaignOptInData, isUserParticipated]);

    const toggleSection = async (sectionId: string) => {
        const isCurrentlyExpanded = expandedSections[sectionId]

        if (isCurrentlyExpanded) {
            setExpandedSections({})
        } else {
            setExpandedSections({ [sectionId]: true })

            setTimeout(() => {
                const element = faqRefs.current[sectionId]
                if (element) {
                    const rightPanel = document.querySelector('[data-scroll-container="right-panel"]') as HTMLElement
                    
                    if (rightPanel && window.innerWidth >= 1024) {
                        const elementPosition = element.getBoundingClientRect().top
                        const rightPanelPosition = rightPanel.getBoundingClientRect().top
                        const offsetPosition = elementPosition - rightPanelPosition + rightPanel.scrollTop - 88

                        rightPanel.scrollTo({
                            top: offsetPosition,
                            behavior: 'smooth'
                        })
                    } else {
                        const elementPosition = element.getBoundingClientRect().top
                        const offsetPosition = elementPosition + window.pageYOffset - 88

                        window.scrollTo({
                            top: offsetPosition,
                            behavior: 'smooth'
                        })
                    }
                }
            }, 100)
        }

        await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
            pageType: PAGE_TYPES.CAR_DROP_PAGE,
            additionalProps: {
                action_type: "tap_btn",
                action_name: sectionId === "eligibility" ? "expand_eligibility" : sectionId === "terms" ? "expand_tnc" : "expand_faq",
                city_name: cityName,
                campaign_stage: campaign_stage, 
                car_policyholder: car_policyholder,
                campaign_name: "car_drop"
            },
        });
    }

    const toggleReadMore = async (sectionId: string) => {
        if (sectionId === 'terms') {
            showLoader();
            await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
                pageType: PAGE_TYPES.CAR_DROP_PAGE,
                additionalProps: {
                    action_type: "tap_btn",
                    action_name: "tap_btn_car_drop_tnc",
                    city_name: cityName,
                    campaign_stage: campaign_stage, 
                    car_policyholder: car_policyholder,
                    campaign_name: "car_drop"
                },
            });
            router.push('/car-drop/terms-and-conditions/')
        } else {
            setShowAllContent(prev => ({
                ...prev,
                [sectionId]: !prev[sectionId],
            }))
        }
    }

    return (
        <div
            className={isContestClosedWithNoUpcoming || !uiConfig.showButton ? styles.removeBottomPadding : uiConfig.useReducedBottomSection ? styles.reducedBottomSection : styles.bottomSection}
        >
            <div className={styles.faqSection}>
                {faqData.map(section => {
                    const isExpanded = !!expandedSections[section.id]
                    const showAll = !!showAllContent[section.id]
                    const itemsToShow = section.id === 'terms' ? 3 : 5
                    const showReadMoreButton = 
                        (section.termsSubSections && section.termsSubSections.length > itemsToShow) ||
                        (section.items && section.items.length > itemsToShow)

                    return (
                        <div
                            key={section.id}
                            className={styles.faqItem}
                            ref={(el) => {
                                faqRefs.current[section.id] = el
                            }}
                        >
                            <button
                                type="button"
                                className={styles.faqHeader}
                                onClick={() => toggleSection(section.id)}
                                aria-expanded={isExpanded}
                            >
                                <p className={styles.faqTitle}>{section.title}</p>
                                <div className={`${styles.faqIcon} ${isExpanded ? styles.expanded : ''}`}>
                                    <Image
                                        src={`${assetLoader()}/icons/chevron_down_light.svg`}
                                        alt={isExpanded ? "Chevron_up_dark" : "Chevron_down_dark"}
                                        width={24}
                                        height={24}
                                        loading="lazy"
                                        className={isExpanded ? styles.rotatedIcon : ""}
                                    />
                                </div>
                            </button>

                            {isExpanded && section.content && section.content.length > 0 && (
                                <div className={styles.faqContent}>
                                    {section.content.map((item, index) => (
                                        <div key={index} className={styles.bulletPoint}>
                                            <ul>
                                                <li>{item}</li>
                                            </ul>
                                        </div>
                                    ))}

                                    {section.helperContent && (
                                        <div className={styles.helperContent}>
                                            <p>
                                                {section.helperContent.text}{' '}
                                                <a href={`mailto:${section.helperContent.link}`} className={styles.helperLink}>
                                                    {section.helperContent.link}
                                                </a>
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {isExpanded && section.termsSubSections && section.termsSubSections.length > 0 && (
                                <div className={styles.termsContainer}>
                                    {section.termsSubSections
                                        .slice(0, showAll ? section.termsSubSections.length : itemsToShow)
                                        .map((subSection, index) => (
                                            <div key={index} className={styles.termsSubSection}>
                                                <p className={styles.termsHeading}>{subSection.heading}</p>
                                                <div className={styles.termsItems}>
                                                    {subSection.items.map((item, itemIndex) => (
                                                        <div key={itemIndex} className={styles.termsBulletPoint}>
                                                            <ul>
                                                                <li>{item}</li>
                                                            </ul>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}

                                    {showReadMoreButton && (
                                        <div className={styles.readMoreButton} onClick={() => toggleReadMore(section.id)}>
                                            <span className={styles.readMoreText}>
                                                {section.id === 'terms' ? 'Read more' : (showAll ? 'Read less' : 'Read more')}
                                            </span>
                                            <div className={`${styles.readMoreIcon} ${section.id !== 'terms' && showAll ? styles.expanded : ''}`}>
                                                <Image
                                                    src={`${assetLoader()}/icons/chevron_down_blue.svg`}
                                                    alt="Chevron_icon"
                                                    width={24}
                                                    height={24}
                                                    loading="lazy"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {section.helperContent && (
                                        <div className={styles.helperContent}>
                                            <p>
                                                {section.helperContent.text}{' '}
                                                <a href={`mailto:${section.helperContent.link}`} className={styles.helperLink}>
                                                    {section.helperContent.link}
                                                </a>
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {isExpanded && section.items && section.items.length > 0 && (
                                <div className={styles.faqItemsContainer}>
                                    {section.items
                                        .slice(0, showAll ? section.items.length : itemsToShow)
                                        .map((item, index) => (
                                            <div key={index} className={styles.faqQuestionCard}>
                                                <p className={styles.faqQuestion}>{item.question}</p>
                                                {Array.isArray(item.answer) ? (
                                                    <div className={styles.faqAnswerList}>
                                                        {item.answer.map((answerItem, answerIndex) => (
                                                            <div key={answerIndex} className={styles.faqAnswerBullet}>
                                                                <ul>
                                                                    <li>{answerItem}</li>
                                                                </ul>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className={styles.faqAnswer}>{item.answer}</p>
                                                )}
                                            </div>
                                        ))}
                                    
                                    {section.items.length > itemsToShow && (
                                        <div className={styles.readMoreButton} onClick={() => toggleReadMore(section.id)}>
                                            <span className={styles.readMoreText}>
                                                {showAll ? 'Read less' : 'Read more'}
                                            </span>
                                            <div className={`${styles.readMoreIcon} ${showAll ? styles.expanded : ''}`}>
                                                <Image
                                                    src={`${assetLoader()}/icons/chevron_down_blue.svg`}
                                                    alt="Chevron_icon"
                                                    width={24}
                                                    height={24}
                                                    loading="lazy"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default BottomSection
